import React, { useState } from 'react';
import { Shield, Users, Database, BarChart3, AlertTriangle, X, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import UserManagementView from './UserManagementView';
import ModerationView from './ModerationView';
import { demoDataService } from '../services/demoDataService';
import { profileService } from '../services/profileService';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../context/LanguageContext';

const AdminView: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { select } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [demoStats, setDemoStats] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Record<string, unknown>[]>([]);
  const [searching, setSearching] = useState(false);
  const [impersonatingUser, setImpersonatingUser] = useState<Record<string, unknown> | null>(null);
  const [, setOriginalAdminToken] = useState<string | null>(null); // To store the original admin token

  // Check if user has admin permissions
  const isAdmin = user?.user_type === 'admin';
  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';

  const getAccountStatusLabel = (status?: string | null) => {
    switch (status) {
      case 'active':
        return select({ es: 'activo', en: 'active' });
      case 'suspended':
        return select({ es: 'suspendido', en: 'suspended' });
      case 'warned':
        return select({ es: 'advertido', en: 'warned' });
      case 'pending':
        return select({ es: 'pendiente', en: 'pending' });
      default:
        return select({ es: 'activo', en: 'active' });
    }
  };

  React.useEffect(() => {
    if (isAdmin || isModerator) {
      loadDemoStats();
    }
  }, [isAdmin, isModerator]);

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setSearching(true);
        try {
          const results = await profileService.searchUsers(searchQuery);
          setSearchResults(results);
        } catch (err) {
          console.error('Error searching users:', err);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const loadDemoStats = async () => {
    try {
      const stats = await demoDataService.getDemoDataStats();
      setDemoStats(stats);
    } catch (err) {
      console.error('Error loading demo stats:', err);
    }
  };

  const handleResetDemoData = async () => {
    if (!window.confirm(select({
      es: '⚠️ ADVERTENCIA: Esto eliminará TODOS los datos demo (usuarios, posts, comentarios, etc.) pero preservará usuarios reales. ¿Continuar?',
      en: '⚠️ WARNING: This will delete ALL demo data (users, posts, comments, etc.) but keep real users. Continue?'
    }))) {
      return;
    }

    if (!window.confirm(select({
      es: '🔴 CONFIRMACIÓN FINAL: Esta acción NO se puede deshacer. ¿Estás seguro de eliminar todos los datos demo?',
      en: '🔴 FINAL CONFIRMATION: This action CANNOT be undone. Are you sure you want to delete all demo data?'
    }))) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await demoDataService.resetDemoData();
      setSuccess(select({
        es: '✅ Datos demo eliminados correctamente',
        en: '✅ Demo data deleted successfully'
      }));
      await loadDemoStats();
    } catch (err) {
      setError(
        select({ es: '❌ Error al eliminar datos demo: ', en: '❌ Error deleting demo data: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDemoData = async () => {
    if (!window.confirm(select({
      es: '¿Crear datos demo? Esto añadirá usuarios, posts y interacciones de ejemplo.',
      en: 'Create demo data? This will add sample users, posts, and interactions.'
    }))) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await demoDataService.createDemoData();
      setSuccess(select({ es: '✅ Datos demo creados correctamente', en: '✅ Demo data created successfully' }));
      await loadDemoStats();
    } catch (err) {
      setError(
        select({ es: '❌ Error al crear datos demo: ', en: '❌ Error creating demo data: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setLoading(false);
    }
  };

  interface ImpersonateTarget { id: string; full_name?: string; username?: string; }

  const handleImpersonate = async (targetUser: ImpersonateTarget) => {
    if (!user) {
      setError('No user logged in to perform impersonation.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setOriginalAdminToken(session.access_token);
        localStorage.setItem('originalAdminToken', session.access_token);
      }

      console.log('Attempting to impersonate user:', targetUser.id);
      console.log('Target user details:', targetUser);
      console.log('Admin session token:', session?.access_token);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ target_user_id: targetUser.id }),
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to impersonate user');
      }

      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: responseData.token,
        refresh_token: responseData.token,
      });

      if (setSessionError) {
        throw setSessionError;
      }

      setImpersonatingUser(targetUser);
      setShowImpersonationModal(false);
      setSearchQuery('');
      setSearchResults([]);
      setSuccess(
        select({
          es: `✅ Impersonando a ${targetUser.full_name} (@${targetUser.username})`,
          en: `✅ Impersonating ${targetUser.full_name} (@${targetUser.username})`
        })
      );
      if (refreshUser) refreshUser();
    } catch (err) {
      setError(
        select({ es: '❌ Error al impersonalizar: ', en: '❌ Error impersonating: ' }) +
        (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
      console.error('Impersonation error:', err);
      const storedOriginalToken = localStorage.getItem('originalAdminToken');
      if (storedOriginalToken) {
        supabase.auth.setSession({ access_token: storedOriginalToken, refresh_token: storedOriginalToken })
          .then(() => {
            localStorage.removeItem('originalAdminToken');
            setOriginalAdminToken(null);
            setImpersonatingUser(null);
            if (refreshUser) refreshUser();
            console.log('Reverted to original admin session due to impersonation failure.');
          })
          .catch(revertErr => console.error('Error reverting session after failed impersonation:', revertErr));
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for existing impersonation on component mount
  React.useEffect(() => {
    const storedOriginalToken = localStorage.getItem('originalAdminToken');
    if (storedOriginalToken && user && user.user_type !== 'admin') {
      // If there's a stored token and current user is not admin, it means we are impersonating
      // We need to fetch the impersonated user's details to display them
      const fetchImpersonatedUser = async () => {
        try {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            const profile = await profileService.getProfile(currentUser.id);
            setImpersonatingUser(profile);
            setOriginalAdminToken(storedOriginalToken);
          }
        } catch (err) {
          console.error('Error fetching impersonated user profile:', err);
          // If fetching fails, clear impersonation state
          localStorage.removeItem('originalAdminToken');
          setOriginalAdminToken(null);
          setImpersonatingUser(null);
        }
      };
      fetchImpersonatedUser();
    }
  }, [user]);


  if (!isAdmin && !isModerator && !impersonatingUser) { // Also allow access if impersonating
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <Shield size={64} className="mx-auto text-red-300 mb-6" />
          <h2 className="text-2xl font-bold text-red-900 mb-4">{select({ es: 'Acceso denegado', en: 'Access denied' })}</h2>
          <p className="text-red-700 text-lg mb-6">
            {select({
              es: 'Solo administradores y moderadores pueden acceder al panel de administración.',
              en: 'Only administrators and moderators can access the admin panel.'
            })}
          </p>
          <div className="bg-white rounded-lg p-4 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-gray-900 mb-2">{select({ es: 'Para obtener acceso:', en: 'To gain access:' })}</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>{select({ es: '• Contacta con un administrador actual', en: '• Contact the current admin team' })}</li>
              <li>{select({ es: '• Solicita permisos de moderación', en: '• Request moderation permissions' })}</li>
              <li>{select({ es: '• Demuestra experiencia en gestión de comunidades', en: '• Show community management experience' })}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl">
            <Shield className="text-white" size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{select({ es: 'Panel de administración', en: 'Admin panel' })}</h1>
            <p className="text-gray-600">{select({ es: 'Gestiona usuarios, contenido y configuración de la plataforma', en: 'Manage users, content, and platform settings' })}</p>
          </div>
        </div>
        
        {/* User Info */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
              alt={user?.full_name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <p className="font-semibold text-gray-900">
                {user?.full_name} (@{user?.username})
              </p>
              <p className="text-sm text-purple-700">
                {select({ es: 'Conectado como', en: 'Logged in as' })} {user?.user_type === 'admin' ? '👑 ' + select({ es: 'Administrador', en: 'Administrator' }) : '🛡️ ' + select({ es: 'Moderador', en: 'Moderator' })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users size={20} />
              <span>{select({ es: 'Gestión de usuarios', en: 'User management' })}</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('moderation')}
            className={`py-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'moderation'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield size={20} />
              <span>{select({ es: 'Moderación', en: 'Moderation' })}</span>
            </div>
          </button>
          
          {isAdmin && (
            <button
              onClick={() => {
                setActiveTab('database');
                loadDemoStats();
              }}
              className={`py-3 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'database'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Database size={20} />
                <span>{select({ es: 'Base de datos', en: 'Database' })}</span>
              </div>
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'users' && (
          <div className="space-y-6">
            <UserManagementView />
            {isAdmin && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Eye className="text-blue-600" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Impersonalizar usuario', en: 'Impersonate user' })}</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {select({
                    es: 'Cambia a la sesión de otro usuario para ver la aplicación desde su perspectiva y ayudar a resolver incidencias.',
                    en: 'Switch to another user’s session to see the app from their perspective and help resolve issues.'
                  })}
                </p>
                <button
                  onClick={() => setShowImpersonationModal(true)}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Eye size={20} />
                    <span>{select({ es: 'Impersonalizar usuario', en: 'Impersonate user' })}</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
        {activeTab === 'moderation' && <ModerationView />}
        
        {activeTab === 'database' && isAdmin && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Database className="text-purple-600" size={24} />
                {select({ es: 'Gestión de base de datos', en: 'Database management' })}
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                  <p className="text-green-700">{success}</p>
                </div>
              )}

              {/* Database Statistics */}
              {demoStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-900">{demoStats.profiles}</div>
                    <div className="text-sm text-blue-700">{select({ es: 'Perfiles', en: 'Profiles' })}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-purple-900">{demoStats.posts}</div>
                    <div className="text-sm text-purple-700">{select({ es: 'Publicaciones', en: 'Posts' })}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-red-900">{demoStats.likes}</div>
                    <div className="text-sm text-red-700">{select({ es: 'Likes', en: 'Likes' })}</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-900">{demoStats.comments}</div>
                    <div className="text-sm text-green-700">{select({ es: 'Comentarios', en: 'Comments' })}</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-900">{demoStats.follows}</div>
                    <div className="text-sm text-yellow-700">{select({ es: 'Seguimientos', en: 'Follows' })}</div>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-indigo-900">{demoStats.notifications}</div>
                    <div className="text-sm text-indigo-700">{select({ es: 'Notificaciones', en: 'Notifications' })}</div>
                  </div>
                </div>
              )}

              {/* Demo Data Management */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Create Demo Data */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <BarChart3 className="text-green-600" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{select({ es: 'Crear datos demo', en: 'Create demo data' })}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {select({
                      es: 'Genera usuarios de ejemplo, publicaciones, comentarios e interacciones para pruebas y demostraciones.',
                      en: 'Generate sample users, posts, comments, and interactions for testing and demos.'
                    })}
                  </p>
                  <button
                    onClick={handleCreateDemoData}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {loading
                      ? select({ es: 'Creando...', en: 'Creating...' })
                      : select({ es: '✨ Crear datos demo', en: '✨ Create demo data' })}
                  </button>
                </div>

                {/* Reset Demo Data - Danger Zone */}
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="text-red-600" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-red-900">{select({ es: 'Zona de peligro', en: 'Danger zone' })}</h3>
                  </div>
                  <p className="text-red-700 mb-6">
                    ⚠️ <strong>{select({ es: 'CUIDADO:', en: 'CAUTION:' })}</strong>{' '}
                    {select({
                      es: 'Esto eliminará TODOS los datos demo pero preservará usuarios reales y sus datos.',
                      en: 'This will delete ALL demo data but will preserve real users and their data.'
                    })}
                  </p>
                  <div className="bg-white border border-red-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-red-900 mb-2">{select({ es: 'Se eliminarán:', en: 'This will remove:' })}</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>{select({ es: '• Usuarios demo (maria_painter, carlos_music, etc.)', en: '• Demo users (maria_painter, carlos_music, etc.)' })}</li>
                      <li>{select({ es: '• Publicaciones de ejemplo y archivos multimedia', en: '• Sample posts and media files' })}</li>
                      <li>{select({ es: '• Comentarios y likes demo', en: '• Demo comments and likes' })}</li>
                      <li>{select({ es: '• Colaboraciones y eventos demo', en: '• Demo collaborations and events' })}</li>
                      <li>{select({ es: '• Notificaciones demo', en: '• Demo notifications' })}</li>
                      <li>{select({ es: '• Datos de transacciones demo', en: '• Demo transaction data' })}</li>
                    </ul>
                  </div>
                  <button
                    onClick={handleResetDemoData}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {loading
                      ? select({ es: 'Eliminando...', en: 'Deleting...' })
                      : select({ es: '🗑️ Eliminar datos demo', en: '🗑️ Delete demo data' })}
                  </button>
                </div>
              </div>

              {/* Database Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Database size={20} />
                  {select({ es: 'Información de la base de datos', en: 'Database information' })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">{select({ es: 'Tablas principales:', en: 'Core tables:' })}</h4>
                    <ul className="text-blue-700 space-y-1">
                      <li>{select({ es: '• profiles (usuarios)', en: '• profiles (users)' })}</li>
                      <li>{select({ es: '• posts (publicaciones)', en: '• posts (publications)' })}</li>
                      <li>{select({ es: '• media_files (archivos)', en: '• media_files (files)' })}</li>
                      <li>{select({ es: '• comments (comentarios)', en: '• comments (comments)' })}</li>
                      <li>{select({ es: '• likes (me gusta)', en: '• likes (likes)' })}</li>
                      <li>{select({ es: '• follows (seguidores)', en: '• follows (followers)' })}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">{select({ es: 'Funciones adicionales:', en: 'Additional features:' })}</h4>
                    <ul className="text-blue-700 space-y-1">
                      <li>{select({ es: '• notifications (notificaciones)', en: '• notifications (notifications)' })}</li>
                      <li>{select({ es: '• collaborations (colaboraciones)', en: '• collaborations (collaborations)' })}</li>
                      <li>{select({ es: '• events (eventos)', en: '• events (events)' })}</li>
                      <li>{select({ es: '• orders/sales (ventas)', en: '• orders/sales (sales)' })}</li>
                      <li>{select({ es: '• stories (historias)', en: '• stories (stories)' })}</li>
                      <li>{select({ es: '• user_analytics (analíticas)', en: '• user_analytics (analytics)' })}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Impersonation Modal */}
      {showImpersonationModal && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <Users className="text-blue-600" size={24} />
                    {select({ es: 'Impersonalizar usuario', en: 'Impersonate user' })}
                  </h2>
                  <p className="text-gray-600 mt-1">{select({ es: 'Ve la aplicación desde la perspectiva de otro usuario', en: 'View the app from another user’s perspective' })}</p>
                </div>
                <button
                  onClick={() => {
                    setShowImpersonationModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
                  <div>
                    <h4 className="font-medium text-amber-900 mb-1">⚠️ {select({ es: 'Impersonalización de usuario', en: 'User impersonation' })}</h4>
                    <div className="text-sm text-amber-800 space-y-1">
                      <p>{select({ es: '• Verás la aplicación exactamente como la ve el usuario seleccionado', en: '• You will see the app exactly as the selected user does' })}</p>
                      <p>{select({ es: '• Tendrás acceso a su contenido, mensajes y configuración', en: '• You will access their content, messages, and settings' })}</p>
                      <p>{select({ es: '• Aparecerá una barra de administrador para volver a tu cuenta', en: '• An admin bar will appear so you can return to your account' })}</p>
                      <p>{select({ es: '• ', en: '• ' })}<strong>{select({ es: 'No hagas cambios', en: 'Do not make changes' })}</strong>{select({ es: ' sin consentimiento del usuario', en: ' without the user’s consent' })}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Buscar usuario por nombre o @usuario', en: 'Search user by name or @username' })}
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Buscar usuarios..."
                  />
                </div>
              </div>

              {/* Search Results */}
              <div className="max-h-80 overflow-y-auto">
                {searching && (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-500 text-sm">{select({ es: 'Buscando usuarios...', en: 'Searching users...' })}</p>
                  </div>
                )}

                {!searching && searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((searchUser) => (
                      <div key={searchUser.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <img
                          src={searchUser.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=48&h=48&fit=crop'}
                          alt={searchUser.full_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{searchUser.full_name}</h4>
                            {searchUser.user_type === 'admin' && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                {select({ es: '👑 Administrador', en: '👑 Admin' })}
                              </span>
                            )}
                            {searchUser.user_type === 'moderator' && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                {select({ es: '🛡️ Moderador', en: '🛡️ Moderator' })}
                              </span>
                            )}
                            {searchUser.user_type === 'artist' && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                {select({ es: '🎨 Artista', en: '🎨 Artist' })}
                              </span>
                            )}
                            {searchUser.is_premium && (
                              <span className="text-yellow-500 text-sm">✨</span>
                            )}
                          </div>
                          <p className="text-gray-600 text-sm">@{searchUser.username}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                searchUser.account_status === 'active' ? 'bg-green-100 text-green-700' :
                                searchUser.account_status === 'suspended' ? 'bg-red-100 text-red-700' :
                                searchUser.account_status === 'warned' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {getAccountStatusLabel(searchUser.account_status)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleImpersonate(searchUser)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye size={16} />
                          {select({ es: 'Impersonalizar', en: 'Impersonate' })}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="text-center py-8">
                    <Users size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">{select({ es: 'No se encontraron usuarios', en: 'No users found' })}</p>
                    <p className="text-sm text-gray-400">{select({ es: 'Intenta con otros términos de búsqueda', en: 'Try different search terms' })}</p>
                  </div>
                )}

                {!searching && searchQuery.length < 2 && (
                  <div className="text-center py-8">
                    <Users size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">{select({ es: 'Escribe al menos 2 caracteres para buscar', en: 'Type at least 2 characters to search' })}</p>
                    <p className="text-sm text-gray-400">{select({ es: 'Busca por nombre completo o nombre de usuario', en: 'Search by full name or username' })}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowImpersonationModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminView;
